<!--
  @component App
  @description Main application component

  Orchestrates the entire Refero application with:
  - Three-panel layout (collections, items, details)
  - Toolbar with search and actions
  - Modal dialogs (collection management, export, import)
  - Context menus for items and collections
  - Notification toasts
-->
<script lang="ts">
  // Stores
  import { isAuthenticated } from '@stores/authStore';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import {
    selectedCollectionKey,
    loadCollections,
    selectCollection,
  } from '@stores/collectionStore';
  import { loadItems, selectItem, selectedItem, setSort } from '@stores/itemStore';
  import {
    showItemContextMenu,
    hideAllContextMenus,
    showCollectionContextMenu,
    hideExportModal,
    showNewItemModal,
    exportModal,
    collectionsPanelWidth,
    detailsPanelWidth,
    setCollectionsPanelWidth,
    setDetailsPanelWidth,
  } from '@stores/uiStore';

  // i18n
  import { t } from '@/i18n';

  // Components
  import AuthFlow from './AuthFlow.svelte';
  import ResponsiveLayout from './layout/ResponsiveLayout.svelte';
  import Toolbar from './layout/Toolbar.svelte';
  import CollectionsPanel from './layout/CollectionsPanel.svelte';
  import ItemsPanel from './layout/ItemsPanel.svelte';
  import DetailsPanel from './layout/DetailsPanel.svelte';
  import ItemContextMenu from './ui/ItemContextMenu.svelte';
  import CollectionContextMenu from './ui/CollectionContextMenu.svelte';
  import CollectionModal from './ui/CollectionModal.svelte';
  import ExportModal from './ui/ExportModal.svelte';
  import NewItemModal from './ui/NewItemModal.svelte';
  import ImportModal from './ui/ImportModal.svelte';
  import ScanModal from './ui/ScanModal.svelte';
  import NotificationContainer from './ui/NotificationContainer.svelte';

  // Services
  import { itemActions } from '@services/itemActionsService';
  import { notify } from '@services/notificationService';

  // Types
  import type { Item, Collection, ItemSortColumn } from '@types';
  import type { MobileView } from './layout/ResponsiveLayout.svelte';
  import type { ISBNSearchResult } from '@services/isbnSearchService';

  // ==========================================================================
  // REFS
  // ==========================================================================

  let authFlow: AuthFlow;

  // ==========================================================================
  // REACTIVE STATEMENTS
  // ==========================================================================

  // Track previous library to detect changes
  let prevLibraryId: number | null = null;
  let prevLibraryType: string | null = null;

  // Load collections when library changes
  $: if ($isAuthenticated && $selectedLibraryId && $selectedLibraryType) {
    if (prevLibraryId !== $selectedLibraryId || prevLibraryType !== $selectedLibraryType) {
      prevLibraryId = $selectedLibraryId;
      prevLibraryType = $selectedLibraryType;
      loadCollections($selectedLibraryId, $selectedLibraryType);
      selectCollection('all');
    }
  }

  // Load items when collection changes
  $: if ($isAuthenticated && $selectedLibraryId && $selectedLibraryType && $selectedCollectionKey) {
    const collectionKey = $selectedCollectionKey === 'all' ? null : $selectedCollectionKey;
    loadItems($selectedLibraryId, $selectedLibraryType, { collectionKey });
  }

  // ==========================================================================
  // EVENT HANDLERS - Toolbar
  // ==========================================================================

  function handleSearch(event: CustomEvent<{ query: string }>) {
    if ($selectedLibraryId && $selectedLibraryType) {
      loadItems($selectedLibraryId, $selectedLibraryType, {
        searchQuery: event.detail.query,
        collectionKey: $selectedCollectionKey === 'all' ? null : $selectedCollectionKey,
      });
    }
  }

  function handleShowSettings() {
    authFlow?.openSettings();
  }

  function handleNewItem() {
    showNewItemModal();
  }

  // ==========================================================================
  // EVENT HANDLERS - Import
  // ==========================================================================

  let showImportModal = false;

  function handleOpenImport() {
    showImportModal = true;
  }

  function handleImportClose() {
    showImportModal = false;
  }

  function handleImportComplete() {
    if ($selectedLibraryId && $selectedLibraryType) {
      const collectionKey = $selectedCollectionKey === 'all' ? null : $selectedCollectionKey;
      loadItems($selectedLibraryId, $selectedLibraryType, { collectionKey });
    }
  }

  // ==========================================================================
  // EVENT HANDLERS - Scan
  // ==========================================================================

  let showScanModal = false;

  function handleOpenScan() {
    showScanModal = true;
  }

  function handleScanClose() {
    showScanModal = false;
  }

  async function handleScanFound(event: CustomEvent<{ result: ISBNSearchResult }>) {
    const { result } = event.detail;

    if (!$selectedLibraryId || !$selectedLibraryType) {
      notify.error($t('import.noLibrarySelected'));
      return;
    }

    // Get current collection (if not "all")
    const collectionKey = $selectedCollectionKey === 'all' ? null : $selectedCollectionKey;

    // Prepare item data for creation
    const itemInput = {
      itemType: result.item.itemType || 'book',
      title: result.item.title || '',
      creators: result.item.creators || [],
      ...result.item,
      // Add to current collection if one is selected
      collections: collectionKey ? [collectionKey] : [],
    };

    // Create the item in Zotero
    const createResult = await itemActions.createItem(
      $selectedLibraryId,
      $selectedLibraryType,
      itemInput
    );

    if (createResult.success) {
      notify.success($t('item.itemCreated'));
      // Refresh items list
      loadItems($selectedLibraryId, $selectedLibraryType, { collectionKey });
    } else {
      notify.error(createResult.error || $t('errors.saveFailed'));
    }
  }

  // ==========================================================================
  // EVENT HANDLERS - Collections
  // ==========================================================================

  function handleCollectionSelect(
    event: CustomEvent<{ collectionKey: string }>,
    closeCollections: () => void
  ) {
    selectCollection(event.detail.collectionKey);
    closeCollections();
  }

  function handleCollectionContextMenu(
    event: CustomEvent<{ x: number; y: number; collection: Collection; isSpecial: boolean }>
  ) {
    const { x, y, collection, isSpecial } = event.detail;
    showCollectionContextMenu({ clientX: x, clientY: y } as MouseEvent, collection, isSpecial);
  }

  function handleCollectionsResize(event: CustomEvent<{ width: number }>) {
    setCollectionsPanelWidth(event.detail.width);
  }

  // ==========================================================================
  // EVENT HANDLERS - Items
  // ==========================================================================

  function handleItemSelect(event: CustomEvent<{ item: Item }>) {
    selectItem(event.detail.item);
  }

  function handleItemContextMenu(event: CustomEvent<{ x: number; y: number; item: Item }>) {
    const { x, y, item } = event.detail;
    showItemContextMenu({ clientX: x, clientY: y } as MouseEvent, item);
  }

  function handleSort(event: CustomEvent<{ column: ItemSortColumn }>) {
    setSort(event.detail.column);
  }

  function handleDetailsResize(event: CustomEvent<{ width: number }>) {
    setDetailsPanelWidth(event.detail.width);
  }

  // ==========================================================================
  // EVENT HANDLERS - Global
  // ==========================================================================

  function handleGlobalMouseDown(event: MouseEvent) {
    // Don't hide context menus if clicking inside them
    const target = event.target as HTMLElement;
    if (target.closest('.context-menu')) {
      return;
    }
    hideAllContextMenus();
  }
</script>

<svelte:window on:mousedown={handleGlobalMouseDown} />

<!-- Auth Flow (initialization + modal) -->
<AuthFlow bind:this={authFlow} />

<!-- Main App with Responsive Layout -->
<ResponsiveLayout
  let:isMobile
  let:isTablet
  let:windowWidth
  let:mobileView
  let:showMobileCollections
  let:navigateTo
  let:toggleCollections
  let:closeCollections
>
  <div class="app" class:mobile={isMobile} class:tablet={isTablet}>
    <!-- Toolbar -->
    <Toolbar
      on:search={handleSearch}
      on:newItem={handleNewItem}
      on:openImport={handleOpenImport}
      on:openScan={handleOpenScan}
      on:showSettings={handleShowSettings}
      on:toggleCollections={toggleCollections}
      {isMobile}
      {isTablet}
    />

    <!-- Main Container -->
    <div class="main-container">
      <!-- Mobile Collections Overlay -->
      {#if (isMobile || isTablet) && showMobileCollections}
        <div class="mobile-overlay" on:click={closeCollections} role="presentation"></div>
        <div class="mobile-collections-panel">
          <CollectionsPanel
            width={300}
            on:selectCollection={(e) => handleCollectionSelect(e, closeCollections)}
            on:collectionContextMenu={handleCollectionContextMenu}
            on:resize={handleCollectionsResize}
          />
        </div>
      {/if}

      <!-- Desktop Collections Panel -->
      {#if !isMobile && !isTablet}
        <CollectionsPanel
          width={$collectionsPanelWidth}
          on:selectCollection={(e) => handleCollectionSelect(e, closeCollections)}
          on:collectionContextMenu={handleCollectionContextMenu}
          on:resize={handleCollectionsResize}
        />
      {/if}

      <!-- Items Panel -->
      {#if !isMobile || mobileView === 'items'}
        <div class="items-panel-wrapper" class:hidden-mobile={isMobile && mobileView !== 'items'}>
          <ItemsPanel
            on:selectItem={handleItemSelect}
            on:itemContextMenu={handleItemContextMenu}
            on:sort={handleSort}
          />
        </div>
      {/if}

      <!-- Details Panel -->
      {#if !isMobile || mobileView === 'details'}
        <div
          class="details-panel-wrapper"
          class:hidden-mobile={isMobile && mobileView !== 'details'}
        >
          <DetailsPanel
            width={isMobile ? windowWidth : $detailsPanelWidth}
            on:resize={handleDetailsResize}
          />
        </div>
      {/if}
    </div>

    <!-- Mobile Bottom Navigation -->
    {#if isMobile}
      <nav class="mobile-nav">
        <button
          class="mobile-nav-item"
          class:active={showMobileCollections}
          on:click={toggleCollections}
        >
          <span class="nav-icon">📚</span>
          <span class="nav-label">{$t('collection.collections')}</span>
        </button>
        <button
          class="mobile-nav-item"
          class:active={mobileView === 'items' && !showMobileCollections}
          on:click={() => navigateTo('items')}
        >
          <span class="nav-icon">📋</span>
          <span class="nav-label">{$t('item.items')}</span>
        </button>
        <button
          class="mobile-nav-item"
          class:active={mobileView === 'details' && !showMobileCollections}
          on:click={() => navigateTo('details')}
          disabled={!$selectedItem}
        >
          <span class="nav-icon">📄</span>
          <span class="nav-label">{$t('detailsPanel.title')}</span>
        </button>
      </nav>
    {/if}
  </div>
</ResponsiveLayout>

<!-- Context Menus -->
<ItemContextMenu />
<CollectionContextMenu />

<!-- Modals -->
<CollectionModal />
<NewItemModal />

<ImportModal
  visible={showImportModal}
  on:close={handleImportClose}
  on:imported={handleImportComplete}
/>

<ExportModal visible={$exportModal.visible} items={$exportModal.items} on:close={hideExportModal} />

<ScanModal isOpen={showScanModal} on:close={handleScanClose} on:found={handleScanFound} />

<!-- Notifications -->
<NotificationContainer />

<style>
  :global(body) {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    background: var(--color-bg-primary, #2b2b2b);
    color: var(--color-text-primary, #e8e8e8);
    overflow: hidden;
  }

  :global(*) {
    box-sizing: border-box;
  }

  .app {
    display: flex;
    flex-direction: column;
    height: 100vh;
  }

  .main-container {
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
  }

  .items-panel-wrapper,
  .details-panel-wrapper {
    display: flex;
    flex: 1;
    min-width: 0;
  }

  .details-panel-wrapper {
    flex: 0 0 auto;
  }

  .hidden-mobile {
    display: none !important;
  }

  /* ==========================================================================
     Mobile Collections Overlay
     ========================================================================== */

  .mobile-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: var(--z-index-modal-backdrop, 900);
  }

  .mobile-collections-panel {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 300px;
    max-width: 85vw;
    z-index: var(--z-index-modal, 1000);
    animation: slideIn 0.2s ease-out;
  }

  .mobile-collections-panel :global(.collections-panel) {
    width: 100% !important;
    max-width: none !important;
    height: 100%;
  }

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  /* ==========================================================================
     Mobile Bottom Navigation
     ========================================================================== */

  .mobile-nav {
    display: flex;
    background: var(--color-bg-tertiary, #3c3c3c);
    border-top: 1px solid var(--color-border, #555);
    padding: 8px 0;
    padding-bottom: max(8px, env(safe-area-inset-bottom));
    flex-shrink: 0;
  }

  .mobile-nav-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    transition: color 0.2s;
    min-height: 56px;
  }

  .mobile-nav-item:hover:not(:disabled) {
    color: var(--color-text-primary, #e8e8e8);
  }

  .mobile-nav-item.active {
    color: var(--color-accent, #5c7cfa);
  }

  .mobile-nav-item:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .nav-icon {
    font-size: 20px;
  }

  .nav-label {
    font-size: 11px;
    font-weight: 500;
  }

  /* ==========================================================================
     Mobile/Tablet Adjustments
     ========================================================================== */

  .app.mobile .items-panel-wrapper,
  .app.mobile .details-panel-wrapper {
    flex: 1;
    width: 100%;
  }

  .app.mobile :global(.details-panel) {
    width: 100% !important;
    max-width: none !important;
    border-left: none;
  }

  .app.mobile :global(.items-panel) {
    min-width: 0;
  }

  @media (max-width: 767px) {
    .main-container {
      flex-direction: column;
    }
  }
</style>
